import React, { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import Path from 'routes/Path';
import { FreecodeLoading } from 'components/FreecodePage/FreecodeLoading/FreecodeLoading';
import FreecodePage from 'components/FreecodePage/FreecodePage';
import { editorTypes, identifiers } from 'components/FreecodePage/FreecodeLayout/FreecodeLayoutConstants';
import { useFreecodeContextLayout, useFreecodeContextRun, useFreecodeContextData } from 'hooks/useContext';
import FreecodeLayout from 'components/FreecodePage/FreecodeLayout/FreecodeLayout';
import useLiveCodingLoadFunction from './useLiveCodingLoadFunction';
import FreecodeLiveCodingSolution from 'components/FreecodePage/FreecodeTabs/FreecodeCodeEditor/FreecodeLiveCodingSolution';
import useFreecodeLivePrimus from 'components/FreecodePage/useFreecodeLivePrimus';

const liveCodingComponents = {
  [identifiers.solution]: <FreecodeLiveCodingSolution />,
};

export default function FreecodeLiveCoding() {
  const { ticketId } = useParams();

  const { setEditorType } = useFreecodeContextLayout();
  const { updateRunResponse } = useFreecodeContextRun();
  const { setTicket } = useFreecodeContextData();

  const sendTicketPrimusRoomRequest = useFreecodeLivePrimus();
  const sendTicketPrimusRoomRequestRef = useRef(sendTicketPrimusRoomRequest);
  sendTicketPrimusRoomRequestRef.current = sendTicketPrimusRoomRequest;

  const { loading, error, setError, setLoading } = useLiveCodingLoadFunction(ticketId);

  let { ticketRunBody, isTicketSubmitted, extendedDeadlineTicket } = useSelector((state) => state.freecode);

  useEffect(() => {
    setEditorType(editorTypes.liveCoding);
  }, [setEditorType]);

  useEffect(() => {
    if (ticketRunBody[ticketId]?.levelOutputs) updateRunResponse(ticketRunBody[ticketId]);
  }, [ticketRunBody, updateRunResponse, ticketId]);

  useEffect(() => {
    if (isTicketSubmitted[ticketId]) {
      setError('Ticket has been submitted');
      setLoading(true);
    }
  }, [isTicketSubmitted, setError, setLoading, ticketId]);

  useEffect(() => {
    if (extendedDeadlineTicket[ticketId]?._id) {
      setTicket(extendedDeadlineTicket[ticketId]);
      toast.info('Candidate has extended deadline for ticket');
    }
  }, [extendedDeadlineTicket, setTicket, ticketId]);

  useEffect(() => {
    const handleBeforeUnload = () => sendTicketPrimusRoomRequestRef.current({ operation: 'leaveTicketRoom' });
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      handleBeforeUnload();
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  return (
    <FreecodePage>
      <FreecodeLoading
        loading={loading}
        errors={[error]}
        errorLinkText="Go to tests page"
        errorLink={Path.landingPage}
      />
      {!loading && <FreecodeLayout parentComponents={liveCodingComponents} />}
    </FreecodePage>
  );
}
